<template>
  <div v-show="!homeScreenStatus && !exerciseCompleted && !gameCompleted" class="game">
    <!-- Points & Lives -->
    <div class="game__points">
      <p>Points: {{ playerScore }}</p>
    </div>
    <div class="game__lives">
      <ul>
        <li v-for="life in currentLives" :key="life">
          <img class="life" src="@/assets/practices/soccer_goal/life.svg" alt="life" />
        </li>
        <li v-for="life in maxLives - currentLives" :key="life + 10">
          <img class="life" src="@/assets/practices/soccer_goal/life_dim.svg" alt="life_dim" />
        </li>
      </ul>
    </div>

    <!-- Question -->
    <div class="game__question">
      <div class="game__question--number">{{ fiveExercises[currentExercise].question[0] }}</div>
      <div class="game__question--sign">
        <p v-show="chosenAnswer">{{ chosenAnswer }}</p>
      </div>
      <div class="game__question--number">{{ fiveExercises[currentExercise].question[1] }}</div>
    </div>

    <!-- Goals -->
    <div id="goal-orange-left" @click="answerSelected('left', fiveExercises[currentExercise].options[0].answer)"></div>
    <div id="goal-purple-left" @click="answerSelected('right', fiveExercises[currentExercise].options[1].answer)"></div>

    <div
      id="goal-orange-center"
      @click="answerSelected('left', fiveExercises[currentExercise].options[0].answer)"
    ></div>
    <div
      id="goal-purple-center"
      @click="answerSelected('right', fiveExercises[currentExercise].options[1].answer)"
    ></div>

    <div id="goal-orange-right" @click="answerSelected('left', fiveExercises[currentExercise].options[0].answer)"></div>
    <div
      id="goal-purple-right"
      @click="answerSelected('right', fiveExercises[currentExercise].options[1].answer)"
    ></div>

    <div id="goal-left-symbol">{{ fiveExercises[currentExercise].options[0].symbol }}</div>
    <div
      v-show="chosenAnswer === '>' && fiveExercises[currentExercise].options[0].answer"
      id="goal-feedback-left-correct"
    ></div>
    <div
      v-show="chosenAnswer === '>' && !fiveExercises[currentExercise].options[0].answer"
      id="goal-feedback-left-incorrect"
    ></div>
    <div id="goal-right-symbol">{{ fiveExercises[currentExercise].options[1].symbol }}</div>
    <div
      v-show="chosenAnswer === '<' && fiveExercises[currentExercise].options[1].answer"
      id="goal-feedback-right-correct"
    ></div>
    <div
      v-show="chosenAnswer === '<' && !fiveExercises[currentExercise].options[1].answer"
      id="goal-feedback-right-incorrect"
    ></div>

    <img id="goal-shadow-left" src="@/assets/practices/soccer_goal/goal_shadow.svg" alt="goal_shadow_left" />
    <img id="goal-shadow-right" src="@/assets/practices/soccer_goal/goal_shadow.svg" alt="goal_shadow_right" />

    <!-- Next Exercise -->
    <button v-show="buttonStatus" class="continue-button" @click="clickContinue">Continue</button>

    <!-- Game Explanation -->
    <div v-show="explanationStatus" class="game__explanation">
      <div class="game__explanation__container">
        <p>{{ fiveExercises[currentExercise].explanation }}</p>
      </div>
      <button class="continue-button" @click="clickContinue">Got It!</button>
    </div>

    <!-- Background -->
    <img id="game-field" src="@/assets/practices/soccer_goal/game_field.svg" alt="field" />
    <img id="bleachers-center" src="@/assets/practices/soccer_goal/bleachers_center.svg" alt="bleachers-center" />
    <img id="bleachers-left" src="@/assets/practices/soccer_goal/bleachers_left.svg" alt="bleachers-left" />
    <img id="bleachers-right" src="@/assets/practices/soccer_goal/bleachers_right.svg" alt="bleachers-right" />
    <div id="overlay"></div>
    <div id="stand-lights"></div>

    <!-- Spectators -->
    <div id="spectators-center-first-correct"></div>
    <div id="spectators-center-second-correct"></div>
    <div id="spectators-left"></div>
    <div id="spectators-right"></div>
    <div id="spectators-center-first-incorrect"></div>
    <div id="spectators-center-second-incorrect"></div>
    <div id="spectators-left-incorrect"></div>
    <div id="spectators-right-incorrect"></div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
// import { Howl } from 'howler';
import lottie from 'lottie-web/build/player/lottie';
import exercises from '@/assets/practices/data/soccer_goal/exercises.json';
import sounds from '@/packages/sounds/index';

let goalOrangeLeftGameAnimation;
let goalOrangeRightGameAnimation;
let goalOrangeCenterGameAnimation;
let goalPurpleLeftGameAnimation;
let goalPurpleRightGameAnimation;
let goalPurpleCenterGameAnimation;
// let standLightsGameAnimation;
let goalFeedbackLeftCorrectGameAnimation;
let goalFeedbackRightCorrectGameAnimation;
let goalFeedbackLeftIncorrectGameAnimation;
let goalFeedbackRightIncorrectGameAnimation;
let spectatorsCenterFirstRowCorrectGameAnimation;
let spectatorsCenterSecondRowCorrectGameAnimation;
let spectatorsLeftCorrectGameAnimation;
let spectatorsRightCorrectGameAnimation;
let spectatorsCenterFirstRowIncorrectGameAnimation;
let spectatorsCenterSecondRowIncorrectGameAnimation;
let spectatorsLeftIncorrectGameAnimation;
let spectatorsRightIncorrectGameAnimation;

export default {
  name: 'Game',
  data: () => ({
    exercises,
    fiveExercises: [],
    render: 0,
    buttonStatus: false,
    answer: null,
    answerActive: false,
    chosenAnswer: '',
  }),
  computed: {
    ...mapGetters({
      correctStatus: 'soccerGoal/correctStatus',
      currentExercise: 'soccerGoal/currentExercise',
      currentLives: 'soccerGoal/currentLives',
      exerciseCompleted: 'soccerGoal/exerciseCompleted',
      explanationStatus: 'soccerGoal/explanationStatus',
      gameCompleted: 'soccerGoal/gameCompleted',
      homeScreenStatus: 'soccerGoal/homeScreenStatus',
      maxLives: 'soccerGoal/maxLives',
      playerScore: 'soccerGoal/playerScore',
    }),
  },
  created() {
    this.fiveExercises = this.getRandom(exercises, 5);
  },
  mounted() {
    this.loadAnimations();
  },
  methods: {
    checkGameOver() {
      if (
        (this.$store.state.soccerGoal.exerciseCompleted && this.$store.state.soccerGoal.currentLives <= 0) ||
        (this.$store.state.soccerGoal.exerciseCompleted && this.$store.state.soccerGoal.currentExercise >= 4)
      ) {
        this.$store.state.soccerGoal.gameCompleted = true;
      } else {
        this.$store.state.soccerGoal.currentExercise += 1;
      }
    },
    clickContinue() {
      sounds.globalButton.play();
      this.resetAnimations();
      // if (this.buttonStatus === 'active') {
      this.answer = null;
      this.chosenAnswer = '';
      this.answerActive = false;
      this.$store.state.soccerGoal.explanationStatus = false;
      this.$store.state.soccerGoal.exerciseCompleted = true;
      this.checkGameOver();
      if (!this.$store.state.soccerGoal.gameCompleted) {
        this.$store.state.soccerGoal.exerciseCompleted = false;
      }
      this.$confetti.stop();
      this.render += 1;
      // this.$store.state.soccerGoal.correctStatus = false;
      this.buttonStatus = false;
      // this.$store.state.soccerGoal.answerStatus = false;
      // } else {
      //   console.log('This button is not active.');
      // }
    },
    getRandom(arr, n) {
      const result = new Array(n);
      let len = arr.length;
      const taken = new Array(len);
      if (n > len) throw new RangeError('getRandom: more elements taken than available');
      // eslint-disable-next-line
      while (n--) {
        const x = Math.floor(Math.random() * len);
        result[n] = arr[x in taken ? taken[x] : x];
        // eslint-disable-next-line
        taken[x] = --len in taken ? taken[len] : len;
      }
      return result;
    },
    answerSelected(goal, answer) {
      if (!this.answerActive) {
        this.answerActive = true;
        this.hideSymbols();
        if (goal === 'left') {
          this.chosenAnswer = '>';
          this.randomGoalOrangeAnimation();
          this.checkAnswer(answer, goal);
        }
        if (goal === 'right') {
          this.chosenAnswer = '<';
          this.randomGoalPurpleAnimation();
          this.checkAnswer(answer, goal);
        } else {
          console.log('There was an error in answerSelected.');
        }
      } else {
        console.log('Answer already selected.');
      }
    },
    opacityCorrectAnimations(opacity) {
      // (0) to hide | (1) to show
      document.getElementById('spectators-center-first-correct').style.opacity = opacity;
      document.getElementById('spectators-center-second-correct').style.opacity = opacity;
      document.getElementById('spectators-left').style.opacity = opacity;
      document.getElementById('spectators-right').style.opacity = opacity;
    },
    opacityIncorrectAnimations(opacity) {
      // (0) to hide | (1) to show
      document.getElementById('spectators-center-first-incorrect').style.opacity = opacity;
      document.getElementById('spectators-center-second-incorrect').style.opacity = opacity;
      document.getElementById('spectators-left-incorrect').style.opacity = opacity;
      document.getElementById('spectators-right-incorrect').style.opacity = opacity;
    },
    opacityGoalAnimations(firstGoal, secondGoal, opacity) {
      document.getElementById(firstGoal).style.opacity = opacity;
      document.getElementById(secondGoal).style.opacity = opacity;
    },
    hideSymbols() {
      document.getElementById('goal-left-symbol').classList.add('symbol-animation-left');
      document.getElementById('goal-right-symbol').classList.add('symbol-animation-right');
    },
    showSymbols() {
      document.getElementById('goal-left-symbol').classList.remove('symbol-animation-left');
      document.getElementById('goal-right-symbol').classList.remove('symbol-animation-right');
    },
    playCorrectAnimations(goal) {
      spectatorsCenterFirstRowCorrectGameAnimation.play();
      spectatorsCenterSecondRowCorrectGameAnimation.play();
      spectatorsLeftCorrectGameAnimation.play();
      spectatorsRightCorrectGameAnimation.play();
      if (goal === 'left') {
        goalFeedbackLeftCorrectGameAnimation.play();
      }
      if (goal === 'right') {
        goalFeedbackRightCorrectGameAnimation.play();
      } else {
        console.log('There was an error in playCorrectAnimations.');
      }
    },
    playIncorrectAnimations(goal) {
      spectatorsCenterFirstRowIncorrectGameAnimation.play();
      spectatorsCenterSecondRowIncorrectGameAnimation.play();
      spectatorsLeftIncorrectGameAnimation.play();
      spectatorsRightIncorrectGameAnimation.play();
      if (goal === 'left') {
        goalFeedbackLeftIncorrectGameAnimation.play();
      }
      if (goal === 'right') {
        goalFeedbackRightIncorrectGameAnimation.play();
      } else {
        console.log('There was an error in playIncorrectAnimations.');
      }
    },
    stopGoalAnimations() {
      goalOrangeLeftGameAnimation.stop();
      goalOrangeRightGameAnimation.stop();
      goalOrangeCenterGameAnimation.stop();
      goalPurpleLeftGameAnimation.stop();
      goalPurpleRightGameAnimation.stop();
      goalPurpleCenterGameAnimation.stop();
    },
    stopCorrectAnimations() {
      spectatorsCenterFirstRowCorrectGameAnimation.stop();
      spectatorsCenterSecondRowCorrectGameAnimation.stop();
      spectatorsLeftCorrectGameAnimation.stop();
      spectatorsRightCorrectGameAnimation.stop();
      goalFeedbackLeftCorrectGameAnimation.stop();
      goalFeedbackRightCorrectGameAnimation.stop();
    },
    stopIncorrectAnimations() {
      spectatorsCenterFirstRowIncorrectGameAnimation.stop();
      spectatorsCenterSecondRowIncorrectGameAnimation.stop();
      spectatorsLeftIncorrectGameAnimation.stop();
      spectatorsRightIncorrectGameAnimation.stop();
      goalFeedbackLeftIncorrectGameAnimation.stop();
      goalFeedbackRightIncorrectGameAnimation.stop();
    },
    resetAnimations() {
      this.stopGoalAnimations();
      this.opacityCorrectAnimations(1);
      this.opacityIncorrectAnimations(1);
      this.opacityGoalAnimations('goal-orange-left', 'goal-purple-left', 1);
      this.opacityGoalAnimations('goal-orange-right', 'goal-purple-right', 1);
      this.opacityGoalAnimations('goal-orange-center', 'goal-purple-center', 1);
      this.stopCorrectAnimations();
      this.stopIncorrectAnimations();
      this.showSymbols();
    },
    randomGoalOrangeAnimation() {
      // GENERATE RANDOM NUMBER
      // IF 1 DO LEFT ANIMATION AND HIDE CENTER & LEFT ANIMATIONS
      // IF 2 DO RIGHT ANIMATION AND HIDE CENTER & RIGHT ANIMATIONS
      // IF 3 DO CENTER ANIMATION AND HIDE LEFT & RIGHT ANIMATION
      // AT CLICK CONTINUE SHOW HIDDEN ANIMATIONS
      const randomNumber = Math.floor(Math.random() * 3);
      if (randomNumber === 0) {
        goalOrangeLeftGameAnimation.play();
        this.opacityGoalAnimations('goal-orange-right', 'goal-orange-center', 0);
      }
      if (randomNumber === 1) {
        goalOrangeRightGameAnimation.play();
        this.opacityGoalAnimations('goal-orange-left', 'goal-orange-center', 0);
      }
      if (randomNumber === 2) {
        goalOrangeCenterGameAnimation.play();
        this.opacityGoalAnimations('goal-orange-left', 'goal-orange-right', 0);
      } else {
        console.log('There was an error in randomGoalOrangeAnimation.');
      }
    },
    randomGoalPurpleAnimation() {
      // GENERATE RANDOM NUMBER
      // IF 1 DO LEFT ANIMATION AND HIDE CENTER & LEFT ANIMATIONS
      // IF 2 DO RIGHT ANIMATION AND HIDE CENTER & RIGHT ANIMATIONS
      // IF 3 DO CENTER ANIMATION AND HIDE LEFT & RIGHT ANIMATION
      // AT CLICK CONTINUE SHOW HIDDEN ANIMATIONS
      const randomNumber = Math.floor(Math.random() * 3);
      if (randomNumber === 0) {
        goalPurpleLeftGameAnimation.play();
        this.opacityGoalAnimations('goal-purple-right', 'goal-purple-center', 0);
      }
      if (randomNumber === 1) {
        goalPurpleRightGameAnimation.play();
        this.opacityGoalAnimations('goal-purple-left', 'goal-purple-center', 0);
      }
      if (randomNumber === 2) {
        goalPurpleCenterGameAnimation.play();
        this.opacityGoalAnimations('goal-purple-left', 'goal-purple-right', 0);
      } else {
        console.log('There was an error in randomGoalPurpleAnimation.');
      }
    },
    loadAnimations() {
      // Goal Animations
      goalOrangeLeftGameAnimation = lottie.loadAnimation({
        container: document.getElementById('goal-orange-left'),
        renderer: 'svg',
        autoplay: false,
        loop: false,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_orange_left.json',
      });
      goalOrangeRightGameAnimation = lottie.loadAnimation({
        container: document.getElementById('goal-orange-right'),
        renderer: 'svg',
        autoplay: false,
        loop: false,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_orange_right.json',
      });
      goalOrangeCenterGameAnimation = lottie.loadAnimation({
        container: document.getElementById('goal-orange-center'),
        renderer: 'svg',
        autoplay: false,
        loop: false,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_orange_center.json',
      });
      goalPurpleLeftGameAnimation = lottie.loadAnimation({
        container: document.getElementById('goal-purple-left'),
        renderer: 'svg',
        autoplay: false,
        loop: false,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_purple_left.json',
      });
      goalPurpleRightGameAnimation = lottie.loadAnimation({
        container: document.getElementById('goal-purple-right'),
        renderer: 'svg',
        autoplay: false,
        loop: false,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_purple_right.json',
      });
      goalPurpleCenterGameAnimation = lottie.loadAnimation({
        container: document.getElementById('goal-purple-center'),
        renderer: 'svg',
        autoplay: false,
        loop: false,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_purple_center.json',
      });

      // Stand Lights Animation
      const isSafari = /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);
      if (!isSafari) {
        lottie.loadAnimation({
          container: document.getElementById('stand-lights'),
          renderer: 'svg',
          autoplay: true,
          loop: true,
          path: '/assets/practices/soccer_goal/animations/soccer_goal_stand_lights.json',
        });
      } else {
        lottie.loadAnimation({
          container: document.getElementById('stand-lights'),
          renderer: 'svg',
          autoplay: true,
          loop: true,
          path: '/assets/practices/soccer_goal/animations/soccer_goal_stand_lights_safari.json',
        });
      }

      // Correct Animations
      spectatorsCenterFirstRowCorrectGameAnimation = lottie.loadAnimation({
        container: document.getElementById('spectators-center-first-correct'),
        renderer: 'svg',
        autoplay: false,
        loop: true,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_spectators_center_correct.json',
      });
      spectatorsCenterSecondRowCorrectGameAnimation = lottie.loadAnimation({
        container: document.getElementById('spectators-center-second-correct'),
        renderer: 'svg',
        autoplay: false,
        loop: true,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_spectators_center_correct.json',
      });
      spectatorsLeftCorrectGameAnimation = lottie.loadAnimation({
        container: document.getElementById('spectators-left'),
        renderer: 'svg',
        autoplay: false,
        loop: true,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_spectators_left_correct.json',
      });
      spectatorsRightCorrectGameAnimation = lottie.loadAnimation({
        container: document.getElementById('spectators-right'),
        renderer: 'svg',
        autoplay: false,
        loop: true,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_spectators_right_correct.json',
      });
      goalFeedbackLeftCorrectGameAnimation = lottie.loadAnimation({
        container: document.getElementById('goal-feedback-left-correct'),
        renderer: 'svg',
        autoplay: false,
        loop: false,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_feedback_correct.json',
      });
      goalFeedbackRightCorrectGameAnimation = lottie.loadAnimation({
        container: document.getElementById('goal-feedback-right-correct'),
        renderer: 'svg',
        autoplay: false,
        loop: false,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_feedback_correct.json',
      });

      // Incorrect Animations
      spectatorsCenterFirstRowIncorrectGameAnimation = lottie.loadAnimation({
        container: document.getElementById('spectators-center-first-incorrect'),
        renderer: 'svg',
        autoplay: false,
        loop: true,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_spectators_center_incorrect.json',
      });
      spectatorsCenterSecondRowIncorrectGameAnimation = lottie.loadAnimation({
        container: document.getElementById('spectators-center-second-incorrect'),
        renderer: 'svg',
        autoplay: false,
        loop: true,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_spectators_center_incorrect.json',
      });
      spectatorsLeftIncorrectGameAnimation = lottie.loadAnimation({
        container: document.getElementById('spectators-left-incorrect'),
        renderer: 'svg',
        autoplay: false,
        loop: true,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_spectators_left_incorrect.json',
      });
      spectatorsRightIncorrectGameAnimation = lottie.loadAnimation({
        container: document.getElementById('spectators-right-incorrect'),
        renderer: 'svg',
        autoplay: false,
        loop: true,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_spectators_right_incorrect.json',
      });
      goalFeedbackLeftIncorrectGameAnimation = lottie.loadAnimation({
        container: document.getElementById('goal-feedback-left-incorrect'),
        renderer: 'svg',
        autoplay: false,
        loop: false,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_feedback_incorrect.json',
      });
      goalFeedbackRightIncorrectGameAnimation = lottie.loadAnimation({
        container: document.getElementById('goal-feedback-right-incorrect'),
        renderer: 'svg',
        autoplay: false,
        loop: false,
        path: '/assets/practices/soccer_goal/animations/soccer_goal_feedback_incorrect.json',
      });
    },
    checkAnswer(answer, goal) {
      setTimeout(() => {
        if (answer) {
          this.buttonStatus = true;
          sounds.soccerGoalCorrectAnswer.play();
          this.opacityIncorrectAnimations(0);
          this.playCorrectAnimations(goal);
          this.$store.state.soccerGoal.playerScore += 300;
          this.$confetti.start({
            defaultSize: 10,
            particlesPerFrame: 0.15,
            particles: [
              {
                type: 'rect',
              },
              {
                type: 'circle',
              },
            ],
          });
        } else {
          sounds.soccerGoalIncorrectAnswer.play();
          this.opacityCorrectAnimations(0);
          this.playIncorrectAnimations(goal);
          this.$store.state.soccerGoal.currentLives -= 1;
          setTimeout(() => {
            this.$store.state.soccerGoal.explanationStatus = true;
          }, 2000);
          this.$confetti.start({
            defaultSize: 10,
            particlesPerFrame: 0.1,
            defaultDropRate: 10,
            particles: [
              {
                type: 'image',
                size: 20,
                dropRate: 5,
                url: '/assets/practices/global/confetti/incorrect_cross_heavy.svg',
              },
            ],
          });
        }
      }, 2000);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/main.scss';

.game {
  @include flex-center;
  background-color: #0d5896;
  background: linear-gradient(to bottom, #0d5896 0%, #819be2 25%, #ffffff 50%, #ffffff 100%);
  height: 100vh;
  position: absolute;
  width: 100vw;

  * {
    z-index: 4;
  }

  &__points,
  &__lives {
    @include flex-center;
    border-radius: 5vw;
    height: 9vh;
    // padding: 0 2vw;
    position: absolute;
    top: 2vh;
  }

  &__points {
    background-color: $soccer-goal-color-text;
    border: 0.75vh solid $soccer-goal-color-main;
    color: $soccer-goal-color-main;
    font-size: 3vw;
    font-family: 'VanillaExtractRegular';
    left: 2vw;
    padding: 0 2vw;
  }

  &__lives {
    right: 2vw;

    ul {
      @include flex-center;
      list-style: none;

      li {
        margin: 1vh 0.5vw;

        .life {
          height: 5.5vh;
          margin-top: 1vh;
        }
      }
    }
  }

  &__question {
    @include flex-center;
    font-size: 3vw;
    position: absolute;
    top: 2vh;

    * {
      margin: 0 0.25em;
    }

    &--sign {
      @include flex-center;
      background-color: $soccer-goal-color-text;
      border: 0.75vh solid $soccer-goal-color-main;
      border-radius: 5vw;
      color: $soccer-goal-color-main;
      min-width: 3em;
      height: 9vh;

      p {
        animation: symbol-scale 2s forwards;
      }
    }
  }

  &__explanation {
    @include flex-center;
    background-color: #000000bb;
    flex-direction: column;
    height: 100vh;
    text-align: center;
    width: 100vw;
    z-index: 6;

    &__container {
      @include flex-center;
      flex-direction: column;
      text-align: center;
      width: 90%;

      p {
        font-size: 2.5vw;
        line-height: 1.5;
      }
    }
  }
}

.continue-button {
  background-color: $soccer-goal-color-text;
  border: none;
  border-radius: 50vh;
  color: $soccer-goal-color-main;
  font-size: 1.2em;
  padding: 0.15em 0.75em;
  position: absolute;
  transform: translate(0, 30vh);
  z-index: 10;
}

#game-field {
  position: absolute;
  transform: translate(0, 20vw);
  width: 101%;
  z-index: 3;
}

#bleachers-center,
#bleachers-left,
#bleachers-right {
  opacity: 1;
  position: absolute;
  z-index: 0;
}

#bleachers-left,
#bleachers-right {
  width: 15vw;
}

#bleachers-center {
  transform: translate(0, -6vw);
  width: 70vw;
}

#bleachers-left {
  left: -2vw;
}

#bleachers-right {
  right: -2vw;
}

#stand-lights {
  opacity: 1;
  position: absolute;
  // top: -1.5vw;
  transform: translate(0.25vw, -11.25vw);
  width: 70vw;
  z-index: 2;
}

#spectators-center-first-correct,
#spectators-center-second-correct,
#spectators-center-first-incorrect,
#spectators-center-second-incorrect,
#spectators-left,
#spectators-left-incorrect,
#spectators-right,
#spectators-right-incorrect {
  opacity: 1;
  position: absolute;
  width: 25vw;
  z-index: 1;
}

#spectators-center-first-correct,
#spectators-center-first-incorrect {
  transform: translate(0, -11vw);
  width: 70vw;
}

#spectators-center-second-correct,
#spectators-center-second-incorrect {
  transform: translate(0, -3vw);
  width: 70vw;
}

#spectators-left,
#spectators-left-incorrect {
  left: -11vw;
}

#spectators-right,
#spectators-right-incorrect {
  right: -10vw;
}

#overlay {
  background-color: #0072b8b3;
  height: 100vh;
  position: absolute;
  width: 100vw;
  z-index: 2;
}

#goal-orange-left,
#goal-orange-right,
#goal-orange-center,
#goal-purple-left,
#goal-purple-right,
#goal-purple-center {
  @include flex-center;
  height: 62.5vh;
  position: absolute;
  bottom: 0;
  width: 78.125vh;
  z-index: 5;
}

#goal-orange-left,
#goal-orange-right,
#goal-orange-center {
  transform: translate(-20vw, 0);
}

#goal-purple-left,
#goal-purple-right,
#goal-purple-center {
  transform: translate(20vw, 0);
}

#goal-shadow-left,
#goal-shadow-right {
  position: absolute;
  // transform: translate(-1.4vw, -0.025vw);
  width: 42vh;
  z-index: 4;
}

#goal-shadow-left {
  transform: translate(-19.5vw, 19.5vh);
}

#goal-shadow-right {
  transform: translate(20vw, 19.5vh);
}

#goal-left-symbol,
#goal-right-symbol {
  @include flex-center;
  background-color: white;
  border: 0.75vh solid black;
  color: black;
  font-size: 1.75em;
  height: 9vh;
  // opacity: 1;
  position: absolute;
  width: 20vh;
  z-index: 5;
}

// #goal-left-symbol,
// #goal-right-symbol,
// #goal-feedback-left-correct,
// #goal-feedback-right-correct,
// #goal-feedback-left-incorrect,
// #goal-feedback-right-incorrect {
//   transform: translate(0vh, -33.5vh);
// }

#goal-left-symbol,
#goal-feedback-left-correct,
#goal-feedback-left-incorrect {
  transform: translate(-20vw, -16vh);
}

#goal-right-symbol,
#goal-feedback-right-correct,
#goal-feedback-right-incorrect {
  transform: translate(20vw, -16vh);
}

#goal-feedback-left-correct,
#goal-feedback-right-correct,
#goal-feedback-left-incorrect,
#goal-feedback-right-incorrect {
  @include flex-center;
  font-size: 1.25em;
  height: 11vh;
  padding: 0 0.25em;
  position: absolute;
  z-index: 5;
}

.symbol-animation-left {
  animation-name: scale-down-left;
  animation-duration: 2s;
  animation-fill-mode: forwards;
  opacity: 1;
  transform-origin: bottom;
}

.symbol-animation-right {
  animation-name: scale-down-right;
  animation-duration: 2s;
  animation-fill-mode: forwards;
  opacity: 1;
  transform-origin: bottom;
}

@keyframes symbol-scale {
  0% {
    font-size: 0;
  }

  100% {
    font-size: 1.5em;
  }
}

@keyframes scale-down-left {
  0% {
    opacity: 1;
    // transform: translate(-2.75vh, -33.5vh) scale(1);
    transform: translate(-20vw, -15.5vh) scale(1);
  }

  100% {
    opacity: 0;
    // transform: translate(-2.75vh, -33.5vh) scale(0);
    transform: translate(-20vw, -15.5vh) scale(0);
  }
}

@keyframes scale-down-right {
  0% {
    opacity: 1;
    transform: translate(20vw, -15.5vh) scale(1);
  }

  100% {
    opacity: 0;
    transform: translate(20vw, -15.5vh) scale(0);
  }
}
</style>
